/**
 * The catalogs of the suppliers (contract v10.49, ADR 0044 of the back). Only «Documentos»
 * shows them, so they live in this feature and not in `shared/tariffs`.
 */
import { api, type Page } from '@/shared/api/client';
import { UserFacingError } from '@/shared/api/errors';

import type { Catalog, CatalogLink, CatalogStarted, UploadLink } from './types';

export interface CatalogListParams {
  supplier_id?: string;
  q?: string;
}

export const catalogKeys = {
  all: ['catalogs'] as const,
  list: (params: CatalogListParams) => ['catalogs', 'list', params] as const,
  detail: (id: string) => ['catalogs', 'detail', id] as const,
};

export const catalogsApi = {
  list: (params: CatalogListParams): Promise<Page<Catalog>> =>
    api.get<Page<Catalog>>('/api/catalogs', { ...params, limit: 200 }),

  get: (id: string): Promise<Catalog> => api.get<Catalog>(`/api/catalogs/${id}`),

  /** The row and the signed link to `PUT` the file to. Nothing is in the bucket yet. */
  start: (supplierId: string, file: File, name: string | null): Promise<CatalogStarted> =>
    api.post<CatalogStarted>('/api/catalogs', {
      supplier_id: supplierId,
      filename: file.name,
      size: file.size,
      name,
    }),

  /** The browser finished: the API looks at what arrived and the worker prepares it. */
  uploaded: (id: string): Promise<Catalog> => api.post<Catalog>(`/api/catalogs/${id}/uploaded`),

  /** A signed link to read the PDF in pieces. A new one when the bucket says 403. */
  link: (id: string): Promise<CatalogLink> => api.get<CatalogLink>(`/api/catalogs/${id}/link`),

  rename: (id: string, name: string): Promise<Catalog> =>
    api.patch<Catalog>(`/api/catalogs/${id}`, { name }),

  remove: (id: string): Promise<void> => api.delete<void>(`/api/catalogs/${id}`),

  retry: (id: string): Promise<Catalog> => api.post<Catalog>(`/api/catalogs/${id}/retry`),
};

export const UPLOAD_FAILED =
  'No se pudo subir el catálogo. Comprueba la conexión y vuelve a intentarlo.';
export const UPLOAD_REFUSED = 'El almacén no aceptó el fichero. Vuelve a subirlo.';

/**
 * The file, from the browser to the bucket, with the signed `PUT` the API gave. Not through
 * `api`: this is not our API but the bucket, on another origin and **without the cookie**
 * (a signed link carries its own authorisation, and the bucket must never see the session).
 * `XMLHttpRequest` and not `fetch` because only it says how much of the body has gone.
 *
 * The `Content-Length` the browser sends is the file's own size, the one the API signed;
 * `Content-Type` comes in `headers`. A cancel rejects with an `AbortError`.
 */
export function putToBucket(
  upload: UploadLink,
  file: File,
  {
    onProgress,
    signal,
  }: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Cancelada', 'AbortError'));
      return;
    }
    const request = new XMLHttpRequest();
    request.open(upload.method ?? 'PUT', upload.url);
    request.withCredentials = false;
    for (const [name, value] of Object.entries(upload.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new UserFacingError(UPLOAD_REFUSED));
    };
    // A dropped line, the bucket without the CORS rule, a link that expired mid-way: the
    // browser says nothing more than «network error» for any of them.
    request.onerror = () => reject(new UserFacingError(UPLOAD_FAILED));
    request.ontimeout = () => reject(new UserFacingError(UPLOAD_FAILED));
    request.onabort = () => reject(new DOMException('Cancelada', 'AbortError'));
    signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(file);
  });
}

export function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export const PIECE_FAILED =
  'No se pudo traer una parte del catálogo. Comprueba la conexión y vuelve a intentarlo.';

export interface SignedLink {
  url: string;
  size: number;
  /** Seconds the link is good for, from when the API gave it. */
  expires_in?: number;
}

/** A link this close to expiring is not used: a piece asked for with it would come back
 * 403 and cost a round trip on a slow line. */
const RENEW_BEFORE_MS = 5 * 60 * 1000;

/** A bucket that ignores `Range` sends the whole file for every piece. Up to this, the
 * piece is cut out of it; past it the catalog would come down whole each time, on a
 * phone, and it is refused instead. */
const WHOLE_FILE_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Reads byte ranges from the bucket with a signed link, and asks the API for another when
 * the link is about to expire or the bucket says it did (it lasts 12 h; a catalog stays
 * open all morning). Several pieces fail at once when it expires: they share one new link.
 *
 * `fetch` straight and not `api`: this is the bucket, another origin, and the session
 * cookie must never travel there (`credentials: 'omit'`).
 */
export class BucketReader {
  #link: Promise<SignedLink> | null = null;
  #renewAt = Number.POSITIVE_INFINITY;

  constructor(
    private readonly newLink: () => Promise<SignedLink>,
    private readonly signal: AbortSignal,
    private readonly now: () => number = Date.now,
  ) {}

  link(): Promise<SignedLink> {
    if (this.#link && this.now() >= this.#renewAt) this.#link = null;
    if (!this.#link) {
      const asked = this.now();
      this.#renewAt = Number.POSITIVE_INFINITY;
      const link = this.newLink().then(
        (fresh) => {
          if (fresh.expires_in) {
            this.#renewAt = asked + Math.max(0, fresh.expires_in * 1000 - RENEW_BEFORE_MS);
          }
          return fresh;
        },
        (error: unknown) => {
          if (this.#link === link) this.#link = null;
          throw error;
        },
      );
      this.#link = link;
    }
    return this.#link;
  }

  /** `[begin, end)`, as pdf.js asks. */
  async read(begin: number, end: number): Promise<Uint8Array> {
    for (let attempt = 0; ; attempt += 1) {
      const current = this.link();
      const { url, size } = await current;
      this.signal.throwIfAborted();
      let response: Response;
      try {
        response = await fetch(url, {
          headers: { Range: `bytes=${begin}-${end - 1}` },
          credentials: 'omit',
          signal: this.signal,
        });
      } catch (error) {
        if (this.signal.aborted) throw error;
        throw new UserFacingError(PIECE_FAILED);
      }
      if (response.status === 206) return new Uint8Array(await response.arrayBuffer());
      if (response.status === 200 && size <= WHOLE_FILE_MAX_BYTES) {
        // A bucket that ignores `Range` sends it all: the piece is in there.
        return new Uint8Array(await response.arrayBuffer()).slice(begin, end);
      }
      // Whatever came is not read: a whole catalog on a phone, or an error page.
      void response.body?.cancel().catch(() => undefined);
      if (response.status === 403 && attempt === 0) {
        if (this.#link === current) this.#link = null;
        continue;
      }
      throw new UserFacingError(PIECE_FAILED);
    }
  }
}
